import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import axios from 'axios'
import { useSelector } from 'react-redux'
import { SupportedBrands } from 'src/enums'
import { UserFeature, UserFeatureList } from 'src/models/user.models'
import { authQueryKeys } from 'src/queries/queries.constants'
import {
  ExtendBaseQueryOptions,
  useExtendQuery,
  useRequestConfig,
} from 'src/queries/queries.utils'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { selectIsAuthenticatedFromState } from 'src/redux/selectors/auth.selectors'

interface QueryUserFeaturesOptions
  extends ExtendBaseQueryOptions<
    { features: UserFeatureList },
    UserFeatureList
  > {}

export const useQueryUserFeatures = (
  features: UserFeature[],
  options?: QueryUserFeaturesOptions,
) => {
  const isAuthenticated = useSelector(selectIsAuthenticatedFromState)
  const { data: user } = useQueryOwnUser({ enabled: isAuthenticated })
  const hasOrganization = !!user?.organizationId

  return useExtendQuery({
    queryKey: authQueryKeys.features(features),
    queryUrl: () => `/features?features=${features.join(',')}`,
    select: (data) => data.features,
    ...options,
    enabled: hasOrganization && (options?.enabled ?? true),
  })
}

export type BrandRedirectResponse = {
  id: string
  name: string
  slug: SupportedBrands
  host: string
}

export const useQueryBrandRedirectSuggestions = (
  options?: Partial<UseQueryOptions<BrandRedirectResponse[]>>,
) => {
  const isAuthenticated = useSelector(selectIsAuthenticatedFromState)
  const { data: user } = useQueryOwnUser({ enabled: isAuthenticated })
  const requestConfig = useRequestConfig()

  return useQuery<BrandRedirectResponse[]>({
    ...options,
    enabled: user?.email !== undefined && (options?.enabled ?? true),
    queryKey: ['brand_suggestions'],
    queryFn: async (): Promise<BrandRedirectResponse[]> => {
      return await axios
        .post(
          '/brandcheck',
          {
            email: user?.email,
          },
          { ...requestConfig, withCredentials: true },
        )
        .then((response) => {
          return response.data.brandCheck
            .suggestedBrands as BrandRedirectResponse[]
        })
    },
  })
}
